import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogoMark } from './Logo';
import { useOrg } from './OrgContext';
import { useUpload } from './UploadProvider';
import { useVoice } from './VoiceModal';
import { useT } from '../i18n';
import '../redesign/fonts';
import { L } from '../redesign/tokens';
import { BigMicIcon, SearchIcon, SettingsIcon, UploadIcon } from '../redesign/icons';
import { businessTypeKey, homeKindFor } from '../redesign/company';
import { useViewportKind } from '../redesign/useViewport';
import SearchBar from '../redesign/SearchBar';

// THE header of the redesigned app: logo, company, the top menu (every page except Home) and the buttons
// Search / Upload / Settings. Every page gets it through AppHeader. Ask is the big mic in the middle of Home and, on every
// other page, a smaller round mic fixed at the bottom right of the screen.

// Which top-menu section a page belongs to (the section's tab stays active at any depth).
export function activeSection(pathname) {
  if (/^\/cases(\/|$)/.test(pathname)) return 'cases';
  if (/^\/(reports|commissions|suppliers|dashboard)(\/|$)/.test(pathname)) return 'reports';
  if (/^\/(inventory|manufacturing)(\/|$)/.test(pathname)) return 'inventory';
  if (/^\/settings(\/|$)/.test(pathname)) return 'settings';
  return null;
}

const METRICS = {
  wide: { height: 120, pad: 48, mark: 48, word: 32 },
  laptop: { height: 104, pad: 40, mark: 48, word: 32 },
  tablet: { height: 96, pad: 28, mark: 40, word: 28 },
  phone: { pad: 16, mark: 36, word: 26 },
};

// Button sizes: Home (Leo's) on desktop; smaller on the other pages, where the menu shares the header; smallest on a phone.
const BUTTON_SIZES = {
  home: { w: 76, h: 50, font: 12, icon: 16, gap: 3 },
  page: { w: 66, h: 44, font: 11, icon: 15, gap: 2 },
  phone: { w: 48, h: 40, font: 10, icon: 14, gap: 1 },
};

function HeaderButton({ Icon, label, onClick, active, expanded, size, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-expanded={expanded === undefined ? undefined : expanded}
      data-testid={testId}
      style={{
        position: 'relative', width: size.w, height: size.h, boxSizing: 'border-box', borderRadius: 10,
        background: active ? L.accent : '#FFFFFF', color: active ? '#FFFFFF' : L.text,
        border: `1px solid ${active ? L.accent : L.border}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: size.gap, padding: 0,
        fontSize: size.font, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', overflow: 'hidden',
      }}
    >
      <Icon size={size.icon} color={active ? '#FFFFFF' : L.text} />
      <span style={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}>{label}</span>
    </button>
  );
}

// "<company> · <type>", as wide as its text. With more than one company (staff, multi-company users) it is a button that
// opens the list.
function CompanyChip({ name, typeLabel, orgs, orgId, setOrgId, label, small }) {
  const [open, setOpen] = useState(false);
  if (!name) return null;
  const multiple = orgs && orgs.length > 1;
  const chip = {
    fontSize: small ? 10.5 : 13, color: L.muted, padding: small ? '3px 7px' : '4px 10px', border: `1px solid ${L.border}`, borderRadius: 999,
    background: L.chipBg, fontFamily: 'inherit', whiteSpace: 'nowrap', width: 'fit-content', maxWidth: small ? '100%' : 280,
    overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box', alignSelf: small ? 'flex-start' : 'center',
  };
  const text = `${name} · ${typeLabel}`;
  if (!multiple) return <div style={chip} data-testid="company-chip">{text}</div>;
  return (
    <div style={{ position: 'relative', maxWidth: '100%', alignSelf: small ? 'flex-start' : 'center' }}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen((v) => !v)}
        style={{ ...chip, cursor: 'pointer', display: 'block' }} data-testid="company-chip">
        {text} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div role="menu" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, minWidth: 220, zIndex: 41, background: L.card, border: `1px solid ${L.border}`, borderRadius: 12, boxShadow: '0 12px 32px rgba(46,33,22,0.14)', overflow: 'hidden' }}>
            {orgs.map((o, i) => (
              <button key={o.id} type="button" role="menuitem"
                onClick={() => { setOpen(false); if (o.id !== orgId) setOrgId(o.id); }}
                style={{ display: 'flex', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '10px 14px', background: o.id === orgId ? L.chipBg : L.card, border: 'none', borderTop: i ? `1px solid ${L.rowLine}` : 'none', fontSize: 14, color: L.text, fontFamily: 'inherit', cursor: 'pointer' }}>
                <span>{o.name || o.slug || o.id}</span>
                {o.id === orgId && <span aria-hidden="true" style={{ color: L.accent }}>✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Ask on every page except Home: the Home mic, a little smaller, always visible at the bottom right.
function AskFloat({ onClick, active, label, phone, dotLabel }) {
  const size = phone ? 60 : 80;
  return (
    <button type="button" onClick={onClick} aria-label={label} data-testid="ask-float" style={{
      position: 'fixed', right: phone ? 16 : 20, bottom: phone ? 16 : 20, width: size, height: size, borderRadius: '50%',
      background: L.accent, border: `${phone ? 5 : 6}px solid ${L.bg}`, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
      justifyContent: 'center', cursor: 'pointer', padding: 0, zIndex: 30, boxShadow: '0 6px 18px rgba(46,33,22,0.3)',
    }}>
      <BigMicIcon size={phone ? 24 : 30} />
      {active && (
        <span data-testid="ask-float-dot" role="img" aria-label={dotLabel}
          style={{ position: 'absolute', top: 0, right: 0, width: 12, height: 12, borderRadius: '50%', background: L.green, border: `2px solid ${L.bg}` }} />
      )}
    </button>
  );
}

function Menu({ items, current, phone, label }) {
  return (
    <nav aria-label={label} style={{ display: 'flex', alignItems: 'center', gap: phone ? 6 : 10 }}>
      {items.map((it) => {
        const on = current === it.key;
        const base = { borderRadius: 10, border: `1px solid ${on ? L.accent : L.border}`, textDecoration: 'none', whiteSpace: 'nowrap' };
        const style = on
          ? { ...base, background: L.accent, color: '#FFFFFF', fontFamily: L.serif, fontStyle: 'italic', fontWeight: 500, fontSize: phone ? 20 : 26, padding: phone ? '8px 16px' : '10px 28px' }
          : { ...base, background: '#FFFFFF', color: L.text, fontSize: phone ? 14 : 15, padding: phone ? '8px 12px' : '10px 16px' };
        return <Link key={it.key} to={it.to} aria-current={on ? 'page' : undefined} style={style}>{it.label}</Link>;
      })}
    </nav>
  );
}

export default function SharedHeader() {
  const t = useT();
  const kind = useViewportKind();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { orgId, orgs, setOrgId, can, capabilities } = useOrg();
  const { openUpload } = useUpload();
  const { openVoice, voiceActive } = useVoice();
  const [searchOpen, setSearchOpen] = useState(false);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  // going to another page closes the bar
  useEffect(() => { setSearchOpen(false); }, [pathname]);

  const m = METRICS[kind];
  const phone = kind === 'phone';
  const isHome = pathname === '/';
  const section = activeSection(pathname);
  const current = (orgs || []).find((o) => o.id === orgId);

  const menu = [{ key: 'home', to: '/', label: t('nav.home') }, { key: 'cases', to: '/cases', label: t('nav.cases') }];
  if (can('reports.financial')) menu.push({ key: 'reports', to: '/reports', label: t('nav.reports') });
  if (homeKindFor(capabilities) === 'distributor') menu.push({ key: 'inventory', to: '/inventory', label: t('nav.inventory') });
  const showMenu = !isHome;

  const markLink = (
    <Link to="/" tabIndex={-1} aria-hidden="true" style={{ display: 'flex', textDecoration: 'none' }}><LogoMark size={m.mark} /></Link>
  );
  const wordmark = (
    <Link to="/" aria-label="Floxir" style={{ textDecoration: 'none', lineHeight: 1 }}>
      <span style={{ fontFamily: L.brand, fontStyle: 'italic', fontWeight: 500, fontSize: m.word, color: L.accent }}>Floxir</span>
    </Link>
  );
  const chip = (
    <CompanyChip name={current && current.name} typeLabel={t(businessTypeKey(capabilities))} orgs={orgs} orgId={orgId} setOrgId={setOrgId} label={t('header.company')} small={phone} />
  );

  // Search, Upload (only with its permission), Settings: one row, on every page.
  const size = phone ? BUTTON_SIZES.phone : isHome ? BUTTON_SIZES.home : BUTTON_SIZES.page;
  const buttons = [
    <HeaderButton key="se" Icon={SearchIcon} label={t('header.search')} onClick={() => setSearchOpen((v) => !v)} expanded={searchOpen} size={size} testId="hdr-search" />,
    can('documents.upload') && <HeaderButton key="up" Icon={UploadIcon} label={t('header.upload')} onClick={homeKindFor(capabilities) === 'agent' ? () => navigate('/upload') : openUpload} size={size} testId="hdr-upload" />,
    <HeaderButton key="st" Icon={SettingsIcon} label={t('header.settings')} onClick={() => navigate('/settings')} active={section === 'settings'} size={size} testId="hdr-settings" />,
  ].filter(Boolean);
  const grid = { display: 'flex', gap: 6, flexShrink: 0 };

  // Search opens Leo's bar under the header (SearchBar): top matches as you type, Enter or "See all results" for the page, Close or Esc to close.
  const searchNote = searchOpen && <SearchBar pad={m.pad} phone={phone} onClose={closeSearch} />;

  const shell = { position: 'relative', fontFamily: L.sans, color: L.text };
  const askFloat = !isHome && <AskFloat onClick={openVoice} active={voiceActive} label={t('home.askByVoice')} dotLabel={t('header.conversation')} phone={phone} />;

  if (phone) {
    // The logo symbol on the left with the wordmark and the company chip beside it (the chip as wide as its text);
    // the three small buttons on the right, level with the logo.
    return (
      <>
      <header style={shell} data-testid="shared-header">
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${L.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }} data-testid="logo-block">
              {markLink}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, minWidth: 0 }}>{wordmark}{chip}</div>
            </div>
            <div style={grid}>{buttons}</div>
          </div>
          {showMenu && <div style={{ marginTop: 12, overflow: 'hidden', display: 'flex', justifyContent: 'center' }} data-testid="menu-row"><Menu items={menu} current={section} phone label={t('header.mainMenu')} /></div>}
        </div>
        {searchNote}
      </header>
      {askFloat}
      </>
    );
  }

  const wide = kind === 'wide';
  return (
    <>
    <header style={shell} data-testid="shared-header">
      <div style={{ position: 'relative', height: m.height, boxSizing: 'border-box', padding: `0 ${m.pad}px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${L.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: kind === 'tablet' ? 10 : 14, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{markLink}{wordmark}</div>
          {chip}
        </div>
        {showMenu && wide && (
          <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}><Menu items={menu} current={section} label={t('header.mainMenu')} /></div>
        )}
        <div style={grid}>{buttons}</div>
      </div>
      {showMenu && !wide && (
        <div style={{ padding: `10px ${m.pad}px`, borderBottom: `1px solid ${L.border}`, display: 'flex', justifyContent: 'center' }}><Menu items={menu} current={section} label={t('header.mainMenu')} /></div>
      )}
      {searchNote}
    </header>
    {askFloat}
    </>
  );
}
